/**
 * The EmailBuilder host for one message body (port of the approved prototype
 * bridge, v2-view-waypoint.js, against the real package and the real API).
 *
 *  - The body's HTML + Liquid string is the only document. It is projected by
 *    `waypointSource.project` on every render; each edit is ONE operation applied
 *    to a fresh projection of the latest body, refused when it can't be
 *    represented exactly (the package decides).
 *  - Undo / redo: an in-memory list of body strings per template, gone when the
 *    screen unmounts. Only consecutive typing in one field of one block within
 *    1.5 s shares a step; at most 100 steps.
 *  - Conditions: the canvas shows one variation of each; the author's pick is
 *    view-only and never saved. No Liquid is evaluated on the client.
 *  - Before anything that changes what the builder shows or saves, the pending
 *    inspector edit is committed to ITS document (`gate`), or the transition is
 *    refused and the typed value stays in its field.
 */
import { useMemo, useRef, useState } from "react";
import { get as getBuilder, waypointSource, type EbOp, type ImportCheck, type OpMeta, type OpResult, type SourceField, type WsProjection } from "@v0y4ge/jrni-email-builder";
import { composeCanvas, isolateRendered } from "./canvas-compose";

export const BODY_BUILDER_ID = "msgb-body-builder";

interface History { u: string[]; r: string[]; lastId: string | null; lastAt: number; lastKind: string | null }

export interface BodyBuilderInput {
  templateId: string;
  title: string;
  html: string;
  setHtml: (next: string) => void;
  /** Published source of the layout this email is sent in, or null. */
  layoutHtml: string | null;
  layoutName: string;
  show: { header: boolean; footer: boolean };
  samples: Readonly<Record<string, string>>;
  fields: SourceField[];
  /** Server render of the current content for the builder's Preview tab, or null while none is available. */
  serverPreviewHtml: string | null;
  readOnly: boolean;
}

export interface BodyBuilderState {
  available: boolean;
  /** The body can't be projected (yet); the builder stays on the last good projection. */
  stale: string | null;
  /** Never projected: the body must be fixed in HTML. */
  unmapped: string | null;
  canvasError: string | null;
  sectionError: boolean;
  projection: WsProjection | null;
  srcdoc: string;
  previewSrcdoc: string;
  groups: { id: string; label: string; variants: { value: string; label: string }[]; value: string; disabled: boolean; onChange: (v: string) => void }[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onOp: (op: EbOp, meta?: OpMeta) => OpResult;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  jsonText: string;
  onImportValidate: (text: string) => ImportCheck;
  onImportApply: (res: ImportCheck) => OpResult;
  /** Source range of a node (for "Edit in HTML"), from a fresh projection of the latest body. */
  sourceRange: (id: string) => { start: number; end: number; line: number } | null;
}

/** Commit the builder's pending field edit. False when it can't be applied; the reason is returned for a notice. */
export function gateBuilder(id: string): { ok: true } | { ok: false; reason: string } {
  const h = getBuilder(id);
  if (!h) return { ok: true };
  const r = h.flush() || { ok: true };
  return r.ok ? { ok: true } : { ok: false, reason: String(r.reason || "") };
}
export function insertIntoBuilder(id: string, raw: string): boolean { const h = getBuilder(id); return !!(h && h.insertToken(raw)); }
export function cancelBuilderEdit(id: string): void { const h = getBuilder(id); if (h) h.cancel(); }

const blankProjection = (fields: SourceField[]) => waypointSource.project("", fields) as WsProjection;

export function useBodyBuilder(input: BodyBuilderInput): BodyBuilderState {
  const { templateId: k, html, fields } = input;
  const hist = useRef(new Map<string, History>());
  const good = useRef(new Map<string, WsProjection>());
  const latest = useRef<{ k: string; body: string } | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [sel, setSel] = useState<{ k: string; id: string | null } | null>(null);
  latest.current = null;

  const H = hist.current.get(k) || { u: [], r: [], lastId: null, lastAt: 0, lastKind: null };
  if (!hist.current.has(k)) hist.current.set(k, H);

  const projected = useMemo(() => waypointSource.project(html, fields), [html, fields]);
  let pj: WsProjection | null = null; let stale: string | null = null; let unmapped: string | null = null;
  if (projected.ok) { pj = projected; good.current.set(k, projected); }
  else {
    const g = good.current.get(k);
    if (g) { pj = g; stale = projected.reason || ""; }
    else { unmapped = projected.reason || ""; }
  }

  const bodyCanvas = useMemo(() => {
    if (!pj) return null;
    const p = pj;
    return waypointSource.canvas(p, (_node, m) => {
      const v = picks[k + "|" + m.id];
      return v && (m.variants || []).some((x) => x.value === v) ? v : (m.variants && m.variants[0] ? m.variants[0].value : null);
    });
  }, [pj, picks, k]);

  const composed = useMemo(() => (bodyCanvas == null ? null : composeCanvas({ layoutHtml: input.layoutHtml, show: input.show, bodyCanvas, samples: input.samples })),
    [bodyCanvas, input.layoutHtml, input.show.header, input.show.footer, input.samples]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = (): string => (latest.current && latest.current.k === k ? latest.current.body : input.html);
  const setBody = (body: string) => { latest.current = { k, body }; input.setHtml(body); };
  const record = (prev: string, id: string | null, kind: string) => {
    const now = Date.now();
    if (!(kind === "typing" && H.lastKind === "typing" && H.lastId === id && now - H.lastAt < 1500)) H.u.push(prev);
    if (H.u.length > 100) H.u.shift();
    H.r = []; H.lastId = id; H.lastKind = kind; H.lastAt = now;
  };
  const onOp = (op: EbOp, meta?: OpMeta): OpResult => {
    if (input.readOnly) return { ok: false, reason: "This message can't be edited." };
    const prev = cur();
    const r = waypointSource.apply(prev, op as Parameters<typeof waypointSource.apply>[1], fields);
    if (r.ok && !r.noop && r.src != null) { record(prev, "id" in op ? op.id : null, meta && meta.typing ? "typing" : "action"); setBody(r.src); }
    if (r.ok && r.select !== undefined) setSel({ k, id: r.select ?? null });
    return { ...r, field: r.field == null ? undefined : String(r.field) } as OpResult;
  };
  const step = (from: string[], to: string[]) => {
    const b = from.pop(); if (b == null) return;
    to.push(cur()); H.lastId = null; H.lastKind = null; setBody(b);
  };

  const sectionError = !!(composed && !composed.ok && composed.sectionError);
  let srcdoc = ""; let canvasError: string | null = null;
  if (composed && composed.ok) srcdoc = composed.srcdoc;
  else if (composed && !composed.ok) canvasError = composed.reason;

  const groups = pj ? Object.keys(pj.nodes).filter((id) => (pj as WsProjection).nodes[id]?.mode === "logic").map((id) => {
    const m = (pj as WsProjection).nodes[id] as { label?: string; variants?: { value: string; label: string | null }[] };
    const variants = (m.variants || []).map((v) => ({ value: v.value, label: v.label || v.value }));
    const picked = picks[k + "|" + id];
    const value = picked && variants.some((v) => v.value === picked) ? picked : variants[0] ? variants[0].value : "";
    return { id, label: m.label || "", variants, value, disabled: false, onChange: (v: string) => setPicks((p) => ({ ...p, [k + "|" + id]: v })) };
  }).filter((g) => g.value) : [];

  const selected = sel && sel.k === k && sel.id && pj && pj.nodes[sel.id] ? sel.id : null;

  return {
    available: true,
    stale,
    unmapped,
    canvasError,
    sectionError,
    projection: pj,
    srcdoc,
    previewSrcdoc: input.serverPreviewHtml ? isolateRendered(input.serverPreviewHtml) : srcdoc,
    groups,
    selectedId: selected,
    onSelect: (id) => setSel({ k, id }),
    onOp,
    canUndo: H.u.length > 0,
    canRedo: H.r.length > 0,
    onUndo: () => step(H.u, H.r),
    onRedo: () => step(H.r, H.u),
    jsonText: pj ? waypointSource.exportJson(pj) : waypointSource.exportJson(blankProjection(fields)),
    onImportValidate: (text: string): ImportCheck => {
      const r = waypointSource.importJson(text, fields);
      if (!r.ok) return r;
      const p2 = waypointSource.project(r.body, fields);
      const preview = p2.ok ? composeCanvas({ layoutHtml: input.layoutHtml, show: input.show, bodyCanvas: waypointSource.canvas(p2, () => null), samples: input.samples }) : null;
      return { ...r, k, previewSrcdoc: preview && preview.ok ? preview.srcdoc : undefined };
    },
    onImportApply: (res: ImportCheck): OpResult => {
      if (!res || !res.ok || res.k !== k || typeof res.body !== "string") return { ok: false, reason: "The selected message changed, so nothing was imported. Open Import JSON again." };
      return onOp({ type: "replaceBody", body: res.body });
    },
    sourceRange: (id: string) => {
      const p2 = waypointSource.project(cur(), fields);
      if (!p2.ok) return null;
      let m = p2.nodes[id];
      if (m && m.nested) m = p2.nodes[m.island];
      return m && !m.nested ? { start: m.start, end: m.end, line: m.line } : null;
    },
  };
}
